// One shared read layer for the practice screens (FXSPEED, UXWALK section 3).
//
// Nine sections each fetched the same ~1 MB workspace, the reminder digest
// and the reference tables on their own, so "Still needed" made 45 requests
// and 6 MB (workspace x6, reminder x11). This adapter sits under the one
// axios instance and does two things, both invisible to callers:
//
//   1. IN-FLIGHT DEDUPE: a GET identical to one already on the wire (same
//      URL, params, identity, capacity) shares its response. Every read.
//   2. A SHORT CACHE (8 s) for the read routes that only change when the
//      practice or the team writes: workspace, reference, reminder, terms,
//      overview, confirmations, needs, questions, current result. Anything
//      that polls (`assessment/status`, `binder/status`) is never cached.
//
// ANY non-GET request drops the whole cache before it leaves and again when
// it settles, and a read that started before a write is never stored, so a
// save is always followed by a fresh read (a hand-merged patch has been
// wrong before, see useWorkspace.ts). A caller that must see the server's
// newest state (a poll that just saw a check finish) sends
// `Cache-Control: no-cache`. Callers receive their own copy of the data, so
// one screen mutating a response can never change another's.
import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

export const READ_TTL_MS = 8000;
const CACHEABLE = /\/(workspace|reference|reminder|terms|overview|confirmations|needs|questions|calendar|portfolios|assessment\/current)$/;
// The med-spa questions list is deliberately left out: `useQuestionsList` (portal/) owns its one
// read and guards against an older reply overtaking a newer one (training-bulk.test.mjs), a
// property that needs a refresh to issue a real second request, never to join one already held.
const NEVER_SHARED = /\/portal\/medspa\/.*\/questions$/;
const IDENTITY_HEADERS = ['Authorization', 'X-User-Id', 'X-Role', 'X-Session-Key', 'X-VerityRx-Capacity', 'X-VerityRx-Preview-Release'];

type Headers = { get?: (n: string) => unknown; delete?: (n: string) => unknown; [k: string]: unknown };

function header(config: InternalAxiosRequestConfig, name: string): string {
  const h = (config.headers ?? {}) as Headers;
  const v = typeof h.get === 'function' ? h.get(name) : h[name];
  return v == null ? '' : String(v);
}

function dropHeader(config: InternalAxiosRequestConfig, name: string) {
  const h = config.headers as Headers | undefined;
  if (!h) return;
  if (typeof h.delete === 'function') h.delete(name);
  else delete h[name];
}

function keyOf(config: InternalAxiosRequestConfig): string {
  const params = config.params ? JSON.stringify(config.params) : '';
  return [config.baseURL ?? '', config.url ?? '', params, ...IDENTITY_HEADERS.map((n) => header(config, n))].join('|');
}

const copyOf = (r: AxiosResponse, config: InternalAxiosRequestConfig): AxiosResponse => ({
  ...r,
  config,
  data: r.data != null && typeof r.data === 'object' ? structuredClone(r.data) : r.data,
});

export function createReadCache(inner: AxiosAdapter, now: () => number = Date.now) {
  const inflight = new Map<string, Promise<AxiosResponse>>();
  const stored = new Map<string, { at: number; response: AxiosResponse }>();
  let epoch = 0;
  const reset = () => {
    epoch += 1;
    inflight.clear();
    stored.clear();
  };

  const adapter: AxiosAdapter = async (config) => {
    const method = (config.method ?? 'get').toLowerCase();
    if (method !== 'get') {
      reset();
      try {
        return await inner(config);
      } finally {
        reset();
      }
    }
    const fresh = /no-cache/i.test(header(config, 'Cache-Control'));
    dropHeader(config, 'Cache-Control');
    // Streams, files and abortable requests are never shared.
    if (config.signal || (config.responseType && config.responseType !== 'json')) return inner(config);
    if (NEVER_SHARED.test(`${config.baseURL ?? ''}${(config.url ?? '').split('?')[0]}`)) return inner(config);
    const key = keyOf(config);
    const cacheable = CACHEABLE.test((config.url ?? '').split('?')[0]);
    if (cacheable && !fresh) {
      const hit = stored.get(key);
      if (hit && now() - hit.at < READ_TTL_MS) return copyOf(hit.response, config);
    }
    const shared = inflight.get(key);
    if (shared && !fresh) return copyOf(await shared, config);
    const startedAt = epoch;
    const request = inner(config);
    inflight.set(key, request);
    try {
      const response = await request;
      if (epoch === startedAt && cacheable) stored.set(key, { at: now(), response });
      return response;
    } finally {
      if (inflight.get(key) === request) inflight.delete(key);
    }
  };
  return Object.assign(adapter, { reset });
}
